import { optional, withDependencies, named } from '@wix/thunderbolt-ioc'
import { IViewerManagerImplementor, ModalCompData, PopupsCompData } from 'thunderbolt-viewer-manager-types'
import _ from 'lodash'
import { name as tpaName, ITpaModal, TpaModalSymbol, ISiteMapDs, SiteMapSymbol, TpaPageConfig } from 'feature-tpa'
import {
	BrowserWindowSymbol,
	FeatureStateSymbol,
	IAppWillLoadPageHandler,
	IPropsStore,
	PageFeatureConfigSymbol,
	Props,
	TpaPopupSymbol,
	ITpaPopup,
} from '@wix/thunderbolt-symbols'
import { HandleDsTpaHandlerOptions, ITpaCompPreviewData } from './types'
import {
	PinnedExternalIdStoreSymbol,
	IPinnedExternalIdStore,
	ITpaHandlersManager,
	TpaHandlersManagerSymbol,
	ITpaContextMapping,
	TpaContextMappingSymbol,
} from 'feature-tpa-commons'
import { DebugApis, TbDebugSymbol } from 'feature-debug'
import { TpaCompPreviewDataSymbol } from './symbols'
import { IPageProvider, LogicalReflectorSymbol } from 'feature-pages'
import { IFeatureState } from 'thunderbolt-feature-state'
import type { TpaFeatureState } from 'feature-tpa'

// TODO:delete once this API is part of the ViewerAPI
type NewAPI = {
	tpa: { getCurrentTpaIds: () => Array<string> }
}

export const ViewerManagerTpaApi = withDependencies(
	[
		LogicalReflectorSymbol,
		TpaContextMappingSymbol,
		named(PageFeatureConfigSymbol, tpaName),
		named(FeatureStateSymbol, tpaName),
		BrowserWindowSymbol,
		Props,
		SiteMapSymbol,
		PinnedExternalIdStoreSymbol,
		TpaCompPreviewDataSymbol,
		optional(TbDebugSymbol),
	],
	(
		pageProvider: IPageProvider,
		{ getTpaComponentPageInfo }: ITpaContextMapping,
		tpaPageConfig: TpaPageConfig,
		featureState: IFeatureState<TpaFeatureState>,
		window: Window,
		props: IPropsStore,
		{ getSiteMap }: ISiteMapDs,
		{ pinExternalId, unpinExternalId }: IPinnedExternalIdStore,
		{ setTpaCompPreviewData, getTpaCompPreviewData }: ITpaCompPreviewData,
		debugApi?: DebugApis
	): NewAPI & IViewerManagerImplementor<'tpa'> & IAppWillLoadPageHandler => {
		const pageInfo = {
			pageId: 'masterPage',
			contextId: 'masterPage',
		}
		return {
			name: 'viewerManagerTpaApi',
			appWillLoadPage({ pageId, contextId }) {
				if (pageId !== 'masterPage') {
					pageInfo.pageId = pageId
					pageInfo.contextId = contextId
				}
			},
			tpa: {
				getCurrentTpaIds: () => Object.keys(tpaPageConfig.widgets),
				getSiteMap,
				setCompState: (compId: string, state: any) => {
					const { preventRefresh } = state
					if (typeof preventRefresh === 'undefined') {
						console.warn('setCompState viewerManagerApi called with an invalid state', state)
						return
					}
					preventRefresh
						? pinExternalId(compId, tpaPageConfig.widgets[compId].externalId)
						: unpinExternalId(compId)
				},
				getTpaCompPreviewData,
				removeAllPopups: async () => {
					const pageReflector = await pageProvider(pageInfo.contextId, pageInfo.pageId)
					pageReflector
						.getAllImplementersOf<ITpaPopup>(TpaPopupSymbol)
						.forEach(({ closeNonPersistentPopups }) => {
							closeNonPersistentPopups()
						})
				},
				removeModal: async () => {
					const pageReflector = await pageProvider(pageInfo.contextId, pageInfo.pageId)
					pageReflector.getAllImplementersOf<ITpaModal>(TpaModalSymbol).forEach(({ closeModal }) => {
						closeModal()
					})
				},
				setTpaCompPreviewData,
				getModalCompData: (): ModalCompData | null => {
					const modalCompId = featureState.get()?.tpaModal?.currentModalId
					if (!modalCompId) {
						return null
					}
					const url = props.get(modalCompId).src
					const origCompId = new URL(url).searchParams.get('origCompId')
					return {
						modalData: {
							url,
							origCompId,
						},
						modalStructure: {
							id: modalCompId,
							componentType: 'wysiwyg.viewer.components.tpapps.TPAModal',
						},
					}
				},
				getPopupsCompData: (): PopupsCompData => {
					const { popups, persistentPopups } = featureState.get().tpaPopup || {}
					const openedPopups = { ...(persistentPopups || {}), ...(popups || {}) }
					return _.mapValues(openedPopups, (oldValue, popupCompId) => {
						const url = props.get(popupCompId).src
						const origCompId = new URL(url).searchParams.get('origCompId')
						return {
							popupData: {
								url,
								origCompId,
							},
							structure: {
								id: popupCompId,
								componentType: 'wysiwyg.viewer.components.tpapps.TPAPopup',
							},
						}
					})
				},
				handleDsTpaHandler: async ({ msg, addCallback, callback }: HandleDsTpaHandlerOptions) => {
					const message = msg // TODO can't change HandleDsTpaHandlerOptions type for some reason :(
					const { compId, callId, type } = message
					const contextId = getTpaComponentPageInfo(compId)?.contextId || 'masterPage'
					if (callback && addCallback) {
						addCallback(callId, callback)
					}

					/* the editor does it's own message origin checkups to ensure handleDsTpaHandler is only invoked from trusted origins.
					We use the current window origin as the message origin to provide real trusted origin to the handleMessage function*/
					const origin = window.location.origin
					if (debugApi) {
						debugApi.tpa.addMessage({ message, compId, contextId, origin })
					}
					const pageReflector = await pageProvider(contextId, pageInfo.pageId)
					const [{ handleMessage }] = pageReflector.getAllImplementersOnPageOf<ITpaHandlersManager>(
						TpaHandlersManagerSymbol
					)
					handleMessage({ source: window, origin, message }).catch((e) => {
						console.error('HandleTpaMessageError', type, contextId, compId, e)
					})
				},
			},
		}
	}
)
